/**
 * CONSAI — Admin Login
 * Design: matches adminpage_login.html from Google Stitch
 */
import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const AdminLogin = () => {
  const { language, toggleLanguage } = useLanguage();

  const handleGoogleSignIn = () => {
    localStorage.setItem('consai_admin_login', 'true');
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Parallax effect for glow orbs
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      const orbs = document.querySelectorAll('.admin-glow-orb');
      orbs.forEach((orb, i) => {
        const depth = (i + 1) * 15;
        orb.style.transform = `translate(${(x - 0.5) * depth}px, ${(y - 0.5) * depth}px)`;
      });
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div
      data-testid="admin-login-page"
      style={{ backgroundColor: '#0A0A0B', minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Manrope, sans-serif', overflowX: 'hidden' }}
    >
      {/* Background Glow Orbs */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
        <div className="admin-glow-orb" style={{ position: 'absolute', width: '600px', height: '600px', top: '-200px', left: '-150px', borderRadius: '50%', background: 'radial-gradient(circle, #4f46e5 0%, transparent 70%)', filter: 'blur(80px)', opacity: 0.35 }} />
        <div className="admin-glow-orb" style={{ position: 'absolute', width: '500px', height: '500px', bottom: '-150px', right: '-100px', borderRadius: '50%', background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)', filter: 'blur(80px)', opacity: 0.3 }} />
      </div>

      {/* Fixed Header */}
      <header style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, backgroundColor: 'rgba(10,10,11,0.85)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* CONSAI Logo */}
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
            <img src="/consai-logo.png" alt="CONSAI" style={{ height: '32px', width: 'auto' }} />
            <span className="horizon-font tracking-widest" style={{ fontSize: '17px' }}>
              <span style={{ color: '#ffffff' }}>CONS</span><span style={{ color: '#d7263d' }}>AI</span>
            </span>
          </a>
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            data-testid="admin-language-toggle"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#c4c7c8', fontFamily: 'Manrope, sans-serif', fontSize: '14px', fontWeight: '500', padding: '6px 12px', borderRadius: '6px', transition: 'color 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#c4c7c8'}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>language</span>
            {language === 'en' ? 'हिंदी' : 'English'}
          </button>
        </div>
      </header>

      {/* Main */}
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '88px 16px 32px', position: 'relative', zIndex: 10 }}>
        <div style={{ width: '100%', maxWidth: '440px' }}>
          {/* Glass Card */}
          <div style={{
            background: 'rgba(17, 17, 17, 0.7)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid #262626',
            borderRadius: '16px',
            padding: '48px 40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            boxShadow: '0 32px 64px -16px rgba(0,0,0,0.6)',
          }}>

            {/* Admin Icon */}
            <div style={{ width: '72px', height: '72px', background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.3)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '28px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '36px', color: '#818cf8' }}>admin_panel_settings</span>
            </div>

            {/* Heading */}
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '32px', fontWeight: '700', color: '#ffffff', marginBottom: '12px', letterSpacing: '-0.02em' }}>
              {language === 'hi' ? 'एडमिन पैनल' : 'Admin Panel'}
            </h1>
            <p style={{ color: '#c4c7c8', fontSize: '15px', lineHeight: '1.6', maxWidth: '320px', marginBottom: '40px' }}>
              {language === 'hi'
                ? 'केवल अधिकृत प्रशासनिक कर्मियों के लिए सुरक्षित प्रवेश द्वार।'
                : 'Secure gateway for authorized administrative personnel only.'}
            </p>

            {/* Google Sign-In Button */}
            <button
              onClick={handleGoogleSignIn}
              data-testid="admin-google-signin-btn"
              style={{
                width: '100%',
                height: '52px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                fontFamily: 'Manrope, sans-serif',
                fontSize: '16px',
                fontWeight: '600',
                color: '#1a1a1a',
                transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.transform = 'none'; }}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
            >
              <svg style={{ width: '20px', height: '20px', flexShrink: 0 }} viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              {language === 'hi' ? 'Google से साइन इन करें' : 'Continue with Google'}
            </button>

            {/* SECURE LOGIN Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '28px 0 20px', width: '100%' }}>
              <div style={{ flex: 1, height: '1px', background: '#262626' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.04)', border: '1px solid #262626', borderRadius: '20px', padding: '4px 12px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '14px', color: '#818cf8' }}>shield</span>
                <span style={{ fontSize: '10px', fontWeight: '700', color: '#8e9192', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Manrope, sans-serif' }}>
                  {language === 'hi' ? 'सुरक्षित लॉगिन' : 'SECURE LOGIN'}
                </span>
              </div>
              <div style={{ flex: 1, height: '1px', background: '#262626' }} />
            </div>

            {/* Policy Links */}
            <p style={{ fontSize: '12px', color: 'rgba(196,199,200,0.5)', lineHeight: '1.6', fontFamily: 'Manrope, sans-serif' }}>
              {language === 'hi' ? 'इस पोर्टल तक पहुंचकर, आप ' : 'By accessing this portal, you agree to the '}
              <button onClick={() => {}} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(196,199,200,0.7)', fontSize: '12px', textDecoration: 'underline', padding: 0, fontFamily: 'Manrope, sans-serif' }}>
                {language === 'hi' ? 'आंतरिक उपयोग नीति' : 'Internal Usage Policy'}
              </button>
              {' '}{language === 'hi' ? 'और ' : 'and '}{' '}
              <button onClick={() => {}} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(196,199,200,0.7)', fontSize: '12px', textDecoration: 'underline', padding: 0, fontFamily: 'Manrope, sans-serif' }}>
                {language === 'hi' ? 'सुरक्षा प्रोटोकॉल' : 'Security Protocol'}
              </button>
              {language === 'hi' ? ' से सहमत हैं।' : '.'}
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ position: 'relative', zIndex: 10, borderTop: '1px solid rgba(255,255,255,0.06)', padding: '20px 24px', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', fontWeight: '700', color: 'rgba(196,199,200,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Manrope, sans-serif' }}>
          © 2026 BUFFETISM IT VENTURES PVT LTD
        </p>
      </footer>
    </div>
  );
};

export default AdminLogin;
